import { useState, useEffect } from "react";
import { TextInput } from "@carbon/react";
import { useMutation, gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import HolidayDatePicker from "../../../Components/holidayDatePicker";

// GraphQL mutation for creating a holiday
const CREATE_HOLIDAY_MUTATION = gql`
    mutation CreateHoliday($name: String!, $startDate: String!, $endDate: String!) {
        createHoliday(name: $name, startDate: $startDate, endDate: $endDate) {
            id
            name
            startDate
            endDate
        }
    }
`;

export default function HolidayAdd() {
    const navigate = useNavigate();
    const [holidayName, setHolidayName] = useState("");
    const [dateRange, setDateRange] = useState([]);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [formIsValid, setFormIsValid] = useState(false);

    // Create holiday mutation
    const [createHoliday] = useMutation(CREATE_HOLIDAY_MUTATION, {
        onCompleted: () => {
            // Redirect to holiday management page after successful creation
            navigate("/admin/holidays");
        },
        onError: (error) => {
            setError(`Fehler beim Erstellen des Feiertags: ${error.message}`);
            setSubmitting(false);
        }
    });

    // Validate form inputs and update form validity
    useEffect(() => {
        const isNameValid = holidayName.trim() !== '';
        const areDatesValid = dateRange && dateRange[0] && dateRange[1];
        setFormIsValid(isNameValid && areDatesValid);
    }, [holidayName, dateRange]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);

        // Validate form
        if (!holidayName.trim()) {
            setError("Bitte geben Sie einen Namen für den Feiertag ein");
            setSubmitting(false);
            return;
        }

        if (!dateRange[0] || !dateRange[1]) {
            setError("Bitte wählen Sie ein Start- und Enddatum");
            setSubmitting(false);
            return;
        }

        // Ensure we use the selected dates without timezone shifting
        // Create new Date objects with time set to noon to avoid timezone issues
        const adjustDate = (date) => {
            const newDate = new Date(date);
            newDate.setHours(12, 0, 0, 0);
            return newDate.toISOString();
        };
        
        const startDate = adjustDate(dateRange[0]);
        const endDate = adjustDate(dateRange[1]);

        // Submit mutation
        try {
            await createHoliday({
                variables: {
                    name: holidayName,
                    startDate,
                    endDate
                }
            });
        } catch (err) {
            setError(`Ein Fehler ist aufgetreten: ${err.message}`);
            setSubmitting(false);
        }
    };

    return (
        <div className="py-4">
            <div className="max-w-2xl">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">Schulfreie Tage hinzufügen</h1>
                
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
                        {error}
                    </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    <TextInput
                        id="holiday-name"
                        labelText="Name des Feiertags/der Ferienzeit"
                        placeholder="z.B. Weihnachtsferien, Osterfeiertag"
                        value={holidayName}
                        onChange={(e) => setHolidayName(e.target.value)}
                        required
                    />
                    
                    <div className="mb-4">
                        <p className="mb-2 font-semibold text-gray-700">Zeitraum</p>
                        <HolidayDatePicker 
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates)}
                        />
                    </div>
                    
                    <div className="flex justify-between pt-4">
                        <button 
                            type="button" 
                            onClick={() => navigate('/admin/holidays')}
                            className="px-5 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || !formIsValid}
                            className={`mainBtn text-white px-5 py-2 ${(submitting || !formIsValid) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {submitting ? 'Wird gespeichert...' : 'Speichern'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
